import React, { useEffect, useState } from 'react'
import { Column } from 'primereact/column'
import { Fieldset } from 'primereact/fieldset'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'

import {
  BadgeStatusEntrega,
  Btn,
  ConfirmDialog,
  CustomCheckbox,
  CustomDataTable,
  CustomDataTableHeader,
  CustomDropdown,
  CustomMultiSelect,
  CustomPanel,
  PageHeader,
  Periodo,
} from '../../../../components'
import {
  hideLoadingBox,
  showLoadingBox,
  showMessageBox,
} from '../../../../store/actions/system.actions'
import { tableCellCentered } from '../../../../assets/js/styles'
import useAssocEntregaState, {
  ASSOC_ENTREGA_LOADING_MESSAGE,
  METODOS_ASSOCIACAO,
} from './AssocEntregaState'
import { buildUfOptions, normalizeUf } from '../../../../services/util/estados.service'
import BeepNfInput from './BeepNfInput'

function AssocEntrega({ dispatch, history }) {
  // Vars
  const { selectedPicking } = history.location.state

  // Hooks
  const { state, localDispatch } = useAssocEntregaState(selectedPicking)
  const [globalFilter, setGlobalFilter] = useState('')

  const [selectedUfs, setSelectedUfs] = useState([])
  const [ufOptions, setUfOptions] = useState([])

  const [selectedCities, setSelectedCities] = useState([])
  const [citiesOptions, setCitiesOptions] = useState([])

  const [selectedBairros, setSelectedBairros] = useState([])
  const [bairrosOptions, setBairrosOptions] = useState([])

  const [selectedInfoComplementar, setSelectedInfoComplementar] = useState([])
  const [infoComplementarOptions, setInfoComplementarOptions] = useState([])

  const [selectedReconfStatusIds, setSelectedReconfStatusIds] = useState([])

  const RECONF_STATUS_OPTIONS = [
    { id: 1, label: 'Aguardando Conferência', name: 'Aguardando Conferência' },
    { id: 16, label: 'Picking Concluído', name: 'Picking Concluído' },
    { id: 2, label: 'Conferido', name: 'Conferido' },
    { id: 4, label: 'Aguardando Carregamento', name: 'Aguardando Carregamento' },
    { id: 5, label: 'Carregada', name: 'Carregada' },
    { id: 9, label: 'Devolução Total', name: 'Devolução Total' },
    { id: 12, label: 'Reentrega', name: 'Reentrega' },
  ]

  const toggleReconfStatus = (statusId) => {
    setSelectedReconfStatusIds(prev =>
      prev.includes(statusId) ? prev.filter(id => id !== statusId) : [...prev, statusId]
    )
  }

  const shouldLoadDeliveriesOnOpen =
    selectedPicking.style !== 'Reconferencia' ||
    selectedPicking.status !== 'Sem Entregas Associadas'

  useEffect(() => {
    if (shouldLoadDeliveriesOnOpen) {
      localDispatch.onLoadingMessageChange(ASSOC_ENTREGA_LOADING_MESSAGE.FETCH_ENTREGAS)
    }
  }, [])

  useEffect(() => {
    switch (state.loadingMessage) {
      case ASSOC_ENTREGA_LOADING_MESSAGE.FETCH_ENTREGAS:
        dispatch(showLoadingBox('Carregando as Entregas . . . ', 'Aguarde'))
        localDispatch.getDeliveries(
          state.periodo.dataInicial,
          state.periodo.dataFinal,
          selectedPicking.style === 'Reconferencia' && selectedReconfStatusIds.length
            ? selectedReconfStatusIds
            : undefined,
        )
        break
      case ASSOC_ENTREGA_LOADING_MESSAGE.ASSOCIAR_ENTREGAS:
        dispatch(showLoadingBox('Associando/Desassociando as Entregas . . . ', 'Aguarde'))
        localDispatch.salvarAssociacaoDasEntregas(state.entregas)
        break
      case ASSOC_ENTREGA_LOADING_MESSAGE.FILTRAR_PLACAS:
        dispatch(showLoadingBox('Filtrando as Placas selecionadas . . .', 'Aguarde'))
        localDispatch.filtrarPorPlacaPrevista()
        break
      default:
        dispatch(hideLoadingBox())
    }
  }, [state.loadingMessage])

  useEffect(() => {
    if (state.errorMessage) {
      dispatch(showMessageBox(null, state.errorMessage))
      localDispatch.resetMessage()
    }
  }, [state.errorMessage])

  useEffect(() => {
    if (state.successMessage) {
      dispatch(showMessageBox(state.successMessage, null))
      localDispatch.resetMessage()
    }
  }, [state.successMessage])

  useEffect(() => {
    const base = state.entregasOriginais || []

    const hasUf = base.some(e => !!e.uf)
    setUfOptions(hasUf ? buildUfOptions(base, 'uf') : [])
    setSelectedUfs([])

    const allCities = [...new Set(base.map(e => e.city_name).filter(Boolean))]
      .sort()
      .map(c => ({ label: c, value: c }))
    setCitiesOptions(allCities)
    setSelectedCities([])

    setBairrosOptions([])
    setSelectedBairros([])

    const allInfoComplementar = [...new Set(base.map(e => e.info_complementar).filter(Boolean))]
      .sort()
      .map(info => ({ label: info, value: info }))
    setInfoComplementarOptions(allInfoComplementar)
    setSelectedInfoComplementar([])
  }, [state.entregasOriginais])

  useEffect(() => {
    const base = state.entregasOriginais || []

    let filtered = base
    if (selectedUfs.length) {
      filtered = filtered.filter(t => selectedUfs.includes(normalizeUf(t.uf)))
    }

    const cities = [...new Set(filtered.map(t => t.city_name).filter(Boolean))]
      .sort()
      .map(c => ({ label: c, value: c }))
    setCitiesOptions(cities)

    setSelectedCities([])
    setBairrosOptions([])
    setSelectedBairros([])
  }, [selectedUfs, state.entregasOriginais])

  useEffect(() => {
    const base = state.entregasOriginais || []

    let filtered = base
    if (selectedUfs.length) {
      filtered = filtered.filter(t => selectedUfs.includes(normalizeUf(t.uf)))
    }
    if (selectedCities.length) {
      filtered = filtered.filter(t => selectedCities.includes(t.city_name))
    }

    const bairros = [...new Set(filtered.map(t => t.district).filter(Boolean))]
      .sort()
      .map(b => ({ label: b, value: b }))
    setBairrosOptions(bairros)
  }, [selectedCities, selectedUfs, state.entregasOriginais])

  const locationFilteredEntregas = React.useMemo(() => {
    let data = state.entregas || []

    if (ufOptions.length && selectedUfs.length) {
      data = data.filter(t => selectedUfs.includes(normalizeUf(t.uf)))
    }
    if (selectedCities.length) {
      data = data.filter(t => selectedCities.includes(t.city_name))
    }
    if (selectedBairros.length) {
      data = data.filter(t => selectedBairros.includes(t.district))
    }
    if (selectedInfoComplementar.length) {
      data = data.filter(t => selectedInfoComplementar.includes(t.info_complementar))
    }
    return data
  }, [
    state.entregas,
    ufOptions.length,
    selectedUfs,
    selectedCities,
    selectedBairros,
    selectedInfoComplementar,
  ])

  const visibleEntregas = React.useMemo(() => {
    const termo = (globalFilter || '').trim().toLowerCase()
    if (!termo) return locationFilteredEntregas

    const camposBusca = [
      'identifier',
      'nomeFantasia',
      'cycle_referencia',
      'city_name',
      'district',
      'qteSkuTotal',
      'weightTotal',
      'status',
    ]
    return locationFilteredEntregas.filter(e =>
      camposBusca.some(campo =>
        String(e[campo] == null ? '' : e[campo])
          .toLowerCase()
          .includes(termo),
      ),
    )
  }, [locationFilteredEntregas, globalFilter])

  const onBeepNfEnter = chave => {
    if (!chave) return

    const entrega = locationFilteredEntregas.find(e => String(e.chave_nf || '').trim() === chave)

    if (!entrega) {
      dispatch(showMessageBox(null, 'Nota Fiscal não encontrada'))
      return
    }

    localDispatch.onBeepNfSelect(entrega.delivery_id)
    dispatch(showMessageBox('Entrega selecionada com sucesso'))
  }

  const pageResetKey = [
    globalFilter,
    selectedUfs,
    selectedCities,
    selectedBairros,
    selectedInfoComplementar,
    state.entregas.length,
  ]
    .map(v => JSON.stringify(v))
    .join('|')

  // Render
  const getPageNumber = () => {
    if (selectedPicking.style === 'Manual') return '4.4.3'
    if (selectedPicking.style === 'Reconferencia') return '4.8.3'
    return '4.3.3'
  }

  return (
    <>
      <PageHeader>
        <span>{getPageNumber()} - Associação de Entregas:</span>
        <strong>
          {selectedPicking ? `${selectedPicking.name} - ${selectedPicking.company_name}` : ''}
        </strong>
      </PageHeader>
      <div style={{ marginTop: 20 }}>
        <CustomPanel
          onNovo={() =>
            localDispatch.onLoadingMessageChange(ASSOC_ENTREGA_LOADING_MESSAGE.ASSOCIAR_ENTREGAS)
          }
          btnLabel="Salvar"
          onVoltar={history.goBack}
        >
          <div className="p-grid">
            <div className="p-col-12">
              {selectedPicking.style === 'Reconferencia' && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 8, fontWeight: 600 }}>
                    Selecione os status das entregas que deseja reconferir:
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
                    {RECONF_STATUS_OPTIONS.map(opt => (
                      <span key={opt.id} style={{ display: 'inline-flex', alignItems: 'center' }}>
                        <CustomCheckbox
                          checked={selectedReconfStatusIds.includes(opt.id)}
                          onChange={() => toggleReconfStatus(opt.id)}
                        >
                          <BadgeStatusEntrega status={opt.name} />
                        </CustomCheckbox>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="p-col-12">
              <Periodo
                periodoInicial={state.periodo.dataInicial}
                periodoFinal={state.periodo.dataFinal}
                onPesquisar={(dataInicial, dataFinal) => {
                  if (selectedPicking.style === 'Reconferencia') {
                    if (selectedReconfStatusIds.length === 0) {
                      dispatch(showMessageBox(null, 'Selecione ao menos um status para pesquisar.'))
                      return
                    }
                  }
                  localDispatch.onPeriodoChange({ dataInicial, dataFinal })
                  localDispatch.onLoadingMessageChange(ASSOC_ENTREGA_LOADING_MESSAGE.FETCH_ENTREGAS)
                }}
              />
            </div>
            <div className="p-col-2" style={{ paddingTop: 30 }}>
              <CustomCheckbox
                checked={state.selecionarTodos}
                onChange={value =>
                  localDispatch.onSelecionarTodosChange(
                    value,
                    visibleEntregas.map(e => e.delivery_id),
                  )
                }
              >
                Selecionar Todos
              </CustomCheckbox>
            </div>
            <div className="p-col-3">
              <CustomMultiSelect
                label={'Filtrar Por Ciclo de Entrega'}
                filter={false}
                onChange={value => localDispatch.onCicloFiltroChange(value)}
                value={state.selectedCiclosFiltros}
                options={state.ciclosParaFiltros}
                marginTop={0}
              />
            </div>
            <div className="p-col-3">
              <CustomMultiSelect
                label={'Filtrar Por Referência'}
                filter={false}
                onChange={value => localDispatch.onReferenciaFiltroChange(value)}
                value={state.selectedReferenciasFiltros}
                options={state.referenciasParaFiltros}
                marginTop={0}
              />
            </div>
            <div className="p-col-4">
              {state.transferParaFiltros.length > 0 && (
                <CustomMultiSelect
                  label={'Filtrar Por Transferência'}
                  filter={false}
                  onChange={value => localDispatch.onTransferFiltroChange(value)}
                  value={state.selectedTransferFiltros}
                  options={state.transferParaFiltros}
                  marginTop={0}
                />
              )}
            </div>
            <div className="p-col-4">
              {state.placasPrevistasFiltro.length > 0 ? (
                <CustomMultiSelect
                  label={'Filtrar Por Placa Prevista'}
                  filter={false}
                  onChange={value =>
                    localDispatch.onLoadingMessageChange(
                      ASSOC_ENTREGA_LOADING_MESSAGE.FILTRAR_PLACAS,
                      value,
                    )
                  }
                  value={state.selectedPlacasPrevistas}
                  options={state.placasPrevistasFiltro}
                  marginTop={0}
                />
              ) : null}
            </div>
            {selectedPicking.style !== 'Reconferencia' && (
              <div className="p-col-3" style={{ paddingTop: 20 }}>
                <CustomCheckbox
                  checked={state.mostrarAssocAutomaticas}
                  onChange={value => localDispatch.onMostrarAssocAutomaticasChange(value)}
                >
                  Mostrar Métodos de Associações Automáticos
                </CustomCheckbox>
              </div>
            )}
            <div
              className="p-col-4"
              style={{
                display: state.mostrarAssocAutomaticas ? 'block' : 'none',
              }}
            >
              <CustomDropdown
                showLabel={true}
                label="Métodos de Associação Automáticos"
                marginTop={0}
                optionLabel="label"
                options={METODOS_ASSOCIACAO}
                value={state.selectedMetodoAssociacao}
                onChange={value => localDispatch.onMetodoAssociacaoChange(value)}
              />
            </div>
            <div
              className="p-col-4"
              style={{
                display: state.selectedMetodoAssociacao.value === 'ANFGP' ? 'block' : 'none',
              }}
            >
              <CustomDropdown
                showLabel={true}
                filter
                label="Grupo de Produto Desejado"
                marginTop={0}
                optionLabel="label"
                options={state.grupoProdutos}
                value={state.selectedGrupoProduto}
                onChange={value => localDispatch.onGrupoProdutoChange(value)}
              />
              <Btn
                style={{ marginTop: 10 }}
                onClick={() => localDispatch.onBotaoAssociarClick(state.selectedGrupoProduto)}
              >
                Associar
              </Btn>
            </div>
            <div className="p-col-12">
              <Fieldset
                legend="Filtro"
                toggleable
                collapsed={true}
                className="p-col-12"
                style={{ marginBottom: 20 }}
              >
                <div style={{ display: 'flex', gap: 16 }}>
                  {ufOptions.length > 0 && (
                    <div className="p-col-2" style={{ minWidth: 200 }}>
                      <CustomMultiSelect
                        marginTop={10}
                        label="Estados"
                        options={ufOptions}
                        value={selectedUfs}
                        onChange={setSelectedUfs}
                      />
                    </div>
                  )}

                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomMultiSelect
                      marginTop={10}
                      label="Cidades"
                      options={citiesOptions}
                      value={selectedCities}
                      onChange={setSelectedCities}
                    />
                  </div>

                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomMultiSelect
                      marginTop={10}
                      label="Bairros"
                      options={bairrosOptions}
                      value={selectedBairros}
                      onChange={setSelectedBairros}
                    />
                  </div>

                  <div className="p-col-2" style={{ minWidth: 200 }}>
                    <CustomMultiSelect
                      marginTop={10}
                      label="Info Complementar"
                      options={infoComplementarOptions}
                      value={selectedInfoComplementar}
                      onChange={setSelectedInfoComplementar}
                    />
                  </div>

                </div>

                <div style={{ display: 'flex', gap: 16 }}>
                  <div className="p-col-4" style={{ minWidth: 200 }}>
                    <BeepNfInput onEnter={onBeepNfEnter} />
                  </div>
                </div>
              </Fieldset>
            </div>
            <div className="p-col-12">
              <CustomDataTable
                value={visibleEntregas}
                paginator
                rows={50}
                pageResetKey={pageResetKey}
                emptyMessage="Não existe entrega disponível para associação"
                loading={state.loadingMessage !== null}
                header={<CustomDataTableHeader onChange={value => setGlobalFilter(value)} />}
              >
                <Column
                  field="selected"
                  style={tableCellCentered(50)}
                  body={entrega => (
                    <CustomCheckbox
                      checked={entrega.selected}
                      onChange={() => localDispatch.onCheckBoxRowClicked(entrega)}
                    />
                  )}
                />
                <Column
                  field="identifier"
                  header="Identificador"
                  style={{ textAlign: 'center' }}
                  sortable
                />
                <Column field="nomeFantasia" header="Nome Fantasia" />
                <Column field="cycle_referencia" header="Referência Ciclo" />
                <Column field="city_name" header="Cidade" />
                <Column field="district" header="Bairro" />
                <Column
                  field="qteSkuTotal"
                  header="Qte. SKU"
                  style={tableCellCentered()}
                  body={entrega => entrega.qteSkuTotal.toLocaleString()}
                />
                <Column
                  field="weightTotal"
                  header="Peso"
                  style={tableCellCentered()}
                  body={entrega => entrega.weightTotal.toLocaleString()}
                />
                <Column
                  field="status"
                  header="Status"
                  style={tableCellCentered()}
                  body={entrega => <BadgeStatusEntrega status={entrega.status} />}
                />
              </CustomDataTable>
            </div>
          </div>
        </CustomPanel>
      </div>
      <ConfirmDialog
        isVisible={state.modalConfirmGruposDiferentes.show}
        title="Atenção"
        content={
          <span>
            Existem produtos de grupos diferentes nas notas:{' '}
            {state.modalConfirmGruposDiferentes.notasGruposDiferentes.join(', ')}. Deseja prosseguir
            com a associação?
            <br />
            <span style={{ fontWeight: 'bold', textAlign: 'center', display: 'block' }}>
              (As notas com grupos diferentes não serão associadas)
            </span>
          </span>
        }
        onCancelar={() => localDispatch.onModalConfirmGruposDiferentesClose()}
        onOk={() => localDispatch.onModalConfirmGruposDiferentesConfirm()}
      />
    </>
  )
}

export default connect()(withRouter(AssocEntrega))
